import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  getQueue,
  getQueueCount,
  removeAction,
  getAllPendingPhotos,
  getPendingPhotosCount,
  removePhoto,
} from "@/lib/offlineDB";
import { api } from "@/App";

const OfflineContext = createContext({
  isOffline: false,
  pendingCount: 0,
  pendingPhotos: 0,
  syncing: false,
  triggerSync: () => {},
});

export const useOffline = () => useContext(OfflineContext);

export function OfflineProvider({ children }) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingPhotos, setPendingPhotos] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);

  // Refresh pending counts
  const refreshCounts = useCallback(async () => {
    try {
      const [qc, pc] = await Promise.all([getQueueCount(), getPendingPhotosCount()]);
      setPendingCount(qc);
      setPendingPhotos(pc);
    } catch {
      // IndexedDB may not be available
    }
  }, []);

  // Listen to online/offline events
  useEffect(() => {
    const goOnline = () => {
      setIsOffline(false);
      toast.success("Connexion rétablie", { id: "offline-status" });
      triggerSync();
    };
    const goOffline = () => {
      setIsOffline(true);
      toast.warning("Mode hors-ligne activé", {
        id: "offline-status",
        duration: 5000,
        description: "Les modifications seront synchronisées au retour de la connexion.",
      });
    };

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []); // triggerSync is stable via useCallback

  // Poll pending counts periodically
  useEffect(() => {
    refreshCounts();
    const interval = setInterval(refreshCounts, 5000);
    return () => clearInterval(interval);
  }, [refreshCounts]);

  // ---- SYNC LOGIC ----
  const triggerSync = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setSyncing(true);

    try {
      // 1. Replay queued API actions
      const queue = await getQueue();
      let syncedActions = 0;
      let failedActions = 0;

      for (const action of queue) {
        try {
          const config = {
            method: action.method,
            url: action.url,
            headers: action.headers || {},
          };
          if (action.body) {
            config.data = action.body;
          }
          await api.request(config);
          await removeAction(action.id);
          syncedActions++;
        } catch (err) {
          // If 404 or 409 (resource deleted/conflict), discard the action
          const status = err?.response?.status;
          if (status === 404 || status === 409 || status === 400) {
            await removeAction(action.id);
            failedActions++;
          } else {
            // Network error — stop sync, will retry later
            break;
          }
        }
      }

      // 2. Upload pending photos
      const photos = await getAllPendingPhotos();
      let syncedPhotos = 0;

      for (const photo of photos) {
        try {
          const formData = new FormData();
          formData.append("file", photo.blob, photo.filename);
          const uploadRes = await api.post("/upload/photo", formData);
          const photoUrl = uploadRes.data.url;

          // Try to attach the photo to its task
          if (photo.tacheId && photo.crId) {
            try {
              // Get current task photos
              const taskRes = await api.get(`/comptes-rendus/${photo.crId}/taches?lot=${photo.lot}`);
              const task = taskRes.data.find((t) => t.id === photo.tacheId);
              if (task) {
                const photos_list = [...(task.photos || []), photoUrl].slice(0, 3);
                await api.put(`/comptes-rendus/${photo.crId}/taches/${photo.tacheId}`, { photos: photos_list });
              }
            } catch {
              // Task might have been deleted — photo is still uploaded
            }
          }

          await removePhoto(photo.id);
          syncedPhotos++;
        } catch (err) {
          if (!navigator.onLine) break;
          // If upload fails for another reason, skip and retry later
        }
      }

      await refreshCounts();

      const total = syncedActions + syncedPhotos;
      if (total > 0) {
        toast.success(`Synchronisation terminée : ${total} élément${total > 1 ? "s" : ""} envoyé${total > 1 ? "s" : ""}`, {
          id: "sync-result",
        });
      }
    } catch (err) {
      console.error("Sync error:", err);
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [refreshCounts]);

  return (
    <OfflineContext.Provider value={{ isOffline, pendingCount, pendingPhotos, syncing, triggerSync, refreshCounts }}>
      {children}
    </OfflineContext.Provider>
  );
}
